import axios, { type AxiosError, type AxiosResponse } from 'axios';
import sIntHttpErrors from '../_json/s_int_httpErrors';
import { LEAD } from '../vars';
import { log, loginUrl } from './AxiosVars';
import { clearSession, getIdFirma, getIdUser, getMitarbeiter4Id } from './Session';
import { getFieldFromArray } from './Texte';
import Toast from './Toast';
import { checkUndefinedNullOrEmpty, isLocalServer, parseJson } from './Tools';

declare module 'axios' {
    interface AxiosRequestConfig {
        customTokenHeaderName?: string;
        customToken?: string;
        noLogging?: boolean;
        noToast?: boolean;
        title?: string;
    }

    interface AxiosResponse {
        file?: File;
    }
}

const axiosInstance = axios.create({
    timeout: 0,
});

axiosInstance.interceptors.response.use(
    async function (serviceResponse: AxiosResponse): Promise<AxiosResponse> {
        if (serviceResponse.data instanceof Blob) {
            const contentDisposition = serviceResponse.headers['content-disposition'] || '';
            const filename = contentDisposition.replace('attachment; filename="', '').replace('"', '');
            const file = new File([serviceResponse.data], !checkUndefinedNullOrEmpty(filename) ? filename : 'Download', { type: serviceResponse.headers['content-type'] });
            return Promise.resolve({ ...serviceResponse, data: URL.createObjectURL(file), file: file });
        } else {
            return Promise.resolve(serviceResponse);
        }
    },
    async function (serviceError: AxiosError): Promise<AxiosError> {
        if (serviceError.code === 'ERR_CANCELED') return Promise.reject(null);
        const newServiceError = Object.assign({}, serviceError);
        if (serviceError.code === 'ECONNABORTED') {
            newServiceError.message = 'Die Anfrage hat zu lange gebraucht, um eine Antwort zu senden! Bitte versuchen Sie es später erneut!';
        }
        if (newServiceError?.response?.data instanceof Blob) {
            const blobText = await newServiceError?.response?.data.text();
            newServiceError.response.data = blobText;
        }
        if ([401, 403].includes(newServiceError?.response?.status) && newServiceError?.config.url.endsWith(loginUrl)) {
            newServiceError.message = 'Benutzerkennung und/oder Passwort falsch!';
        } else if (typeof newServiceError?.response?.data === 'string') {
            if (newServiceError?.response?.data?.includes('</html>')) {
                newServiceError.message = getFieldFromArray(sIntHttpErrors, 't', newServiceError?.response?.status, 'i', 'Unbekannter Fehler') as string;
            } else {
                newServiceError.message = newServiceError.response.data;
            }
        } else {
            newServiceError.message = JSON.stringify(newServiceError?.response?.data);
        }
        if (!checkUndefinedNullOrEmpty(newServiceError.config.data)) {
            newServiceError.config.data = parseJson(newServiceError.config.data);
        }

        console.error(newServiceError);

        if (!newServiceError?.config?.noToast) {
            Toast.addError({
                key: newServiceError?.config?.url,
                primary: 'Fehler beim ' + newServiceError?.config?.title,
                secondary: newServiceError?.message,
                tertiary: isLocalServer() ? newServiceError?.stack : undefined,
            });
        }

        if (!newServiceError?.config?.noLogging) {
            log(getIdFirma() || 0, getMitarbeiter4Id(getIdUser())?.sub || 0, 2, JSON.stringify(newServiceError)).then(() => console.log('Fehler wurde gesendet!'));
        }

        if (
            LEAD &&
            [401, 403].includes(newServiceError?.response?.status) &&
            !newServiceError?.config.url.endsWith(loginUrl) &&
            newServiceError?.config.url !== 'https://httpstat.us/' + newServiceError?.response?.status
        ) {
            clearSession();
            if (location.pathname !== '/login') {
                location.replace('/login');
            }
        }

        return Promise.reject(newServiceError);
    }
);

export default axiosInstance;
